<script setup>
import { ref, computed, watch } from 'vue'
import { CheckCircle, XCircle, Loader2, AlertTriangle, Download } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  features: {
    type: Array,
    default: () => []
  },
  isUploading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'cancel', 'retry', 'download-failed'])

// Upload states
const uploadStates = ref(new Map()) // feature.id -> { status, error, progress }
const startTime = ref(null)
const endTime = ref(null)

// Computed properties
const totalCount = computed(() => props.features.length)
const uploadedCount = computed(() =>
  Array.from(uploadStates.value.values()).filter(state => state.status === 'success').length
)
const failedCount = computed(() =>
  Array.from(uploadStates.value.values()).filter(state => state.status === 'error').length
)
const uploadingCount = computed(() =>
  Array.from(uploadStates.value.values()).filter(state => state.status === 'uploading').length
)
const pendingCount = computed(() =>
  totalCount.value - uploadedCount.value - failedCount.value - uploadingCount.value
)

const overallProgress = computed(() => {
  if (totalCount.value === 0) return 0
  return Math.round((uploadedCount.value / totalCount.value) * 100)
})

const isComplete = computed(() => {
  return uploadedCount.value + failedCount.value === totalCount.value
})

const elapsedTime = computed(() => {
  if (!startTime.value) return 0
  const end = endTime.value || Date.now()
  return Math.round((end - startTime.value) / 1000)
})

const estimatedTimeRemaining = computed(() => {
  if (!startTime.value || uploadedCount.value === 0) return null

  const elapsed = elapsedTime.value
  const rate = uploadedCount.value / elapsed
  const remaining = totalCount.value - uploadedCount.value

  return Math.round(remaining / rate)
})

const failedFeatures = computed(() => {
  return props.features.filter(feature =>
    uploadStates.value.get(feature.properties.local_uuid)?.status === 'error'
  )
})

// Methods
const initializeUpload = () => {
  startTime.value = Date.now()
  endTime.value = null
  uploadStates.value.clear()

  // Initialize all features as pending
  props.features.forEach(feature => {
    uploadStates.value.set(feature.properties.local_uuid, {
      status: 'pending',
      progress: 0,
      error: null
    })
  })
}

const updateFeatureStatus = (featureId, status, error = null, progress = 0) => {
  const currentState = uploadStates.value.get(featureId) || {}
  uploadStates.value.set(featureId, {
    ...currentState,
    status,
    error,
    progress
  })

  // Check if upload is complete
  if (isComplete.value && !endTime.value) {
    endTime.value = Date.now()
  }
}

const getStatusIcon = (status) => {
  switch (status) {
    case 'success':
      return CheckCircle
    case 'error':
      return XCircle
    case 'uploading':
      return Loader2
    default:
      return AlertTriangle
  }
}

const getStatusColor = (status) => {
  switch (status) {
    case 'success':
      return 'text-green-500'
    case 'error':
      return 'text-red-500'
    case 'uploading':
      return 'text-blue-500'
    default:
      return 'text-gray-500'
  }
}

const formatTime = (seconds) => {
  if (!seconds) return '0s'

  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60

  if (mins > 0) {
    return `${mins}m ${secs}s`
  }
  return `${secs}s`
}

const handleClose = () => {
  if (props.isUploading) {
    emit('cancel')
  } else {
    emit('close')
  }
}

const handleRetryFailed = () => {
  emit('retry', failedFeatures.value)
}

// Watch for upload start/stop
watch(() => props.isUploading, (isUploading) => {
  if (isUploading) {
    initializeUpload()
  }
})

// Expose methods for parent component
defineExpose({
  updateFeatureStatus
})
</script>

<template>
  <Dialog :open="open" @update:open="handleClose">
    <DialogContent class="max-w-2xl max-h-[80vh]">
      <DialogHeader>
        <DialogTitle class="flex items-center space-x-2">
          <component
            :is="isUploading ? Loader2 : (isComplete ? CheckCircle : AlertTriangle)"
            :class="[
              'w-5 h-5',
              isUploading ? 'animate-spin text-blue-500' :
              isComplete ? 'text-green-500' : 'text-gray-500'
            ]"
          />
          <span>Upload Progress</span>
        </DialogTitle>
      </DialogHeader>

      <div class="space-y-6">
        <!-- Overall Progress -->
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span class="text-sm font-medium">Overall Progress</span>
            <span class="text-sm text-gray-600">{{ overallProgress }}%</span>
          </div>
          <!-- Native Progress Bar -->
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
              :style="{ width: overallProgress + '%' }"
            ></div>
          </div>
          <div class="flex justify-between text-xs text-gray-500">
            <span>{{ uploadedCount }} of {{ totalCount }} uploaded</span>
            <span v-if="isUploading && estimatedTimeRemaining">
              ~{{ formatTime(estimatedTimeRemaining) }} remaining
            </span>
            <span v-else-if="isComplete">
              Completed in {{ formatTime(elapsedTime) }}
            </span>
          </div>
        </div>

        <!-- Status Summary -->
        <div class="flex items-center justify-center space-x-4">
          <Badge variant="secondary" class="text-green-600">
            <CheckCircle class="w-3 h-3 mr-1" />
            {{ uploadedCount }} Success
          </Badge>
          <Badge variant="secondary" class="text-red-600" v-if="failedCount > 0">
            <XCircle class="w-3 h-3 mr-1" />
            {{ failedCount }} Failed
          </Badge>
          <Badge variant="secondary" class="text-blue-600" v-if="uploadingCount > 0">
            <Loader2 class="w-3 h-3 mr-1 animate-spin" />
            {{ uploadingCount }} Uploading
          </Badge>
          <Badge variant="secondary" class="text-gray-600" v-if="pendingCount > 0">
            <AlertTriangle class="w-3 h-3 mr-1" />
            {{ pendingCount }} Pending
          </Badge>
        </div>

        <!-- Feature List -->
        <div class="space-y-2">
          <h4 class="text-sm font-medium">Features</h4>
          <!-- Native Scrollable Area -->
          <div class="h-64 border rounded overflow-auto">
            <div class="p-2 space-y-1">
              <div
                v-for="feature in features"
                :key="feature.properties.local_uuid"
                class="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
              >
                <div class="flex items-center space-x-2 flex-1 min-w-0">
                  <component
                    :is="getStatusIcon(uploadStates.get(feature.properties.local_uuid)?.status || 'pending')"
                    :class="[
                      'w-4 h-4 flex-shrink-0',
                      getStatusColor(uploadStates.get(feature.properties.local_uuid)?.status || 'pending'),
                      uploadStates.get(feature.properties.local_uuid)?.status === 'uploading' ? 'animate-spin' : ''
                    ]"
                  />
                  <span class="truncate">
                    {{ feature.properties.name || feature.properties.fileName || 'Unnamed' }}
                  </span>
                </div>

                <div class="flex items-center space-x-2 text-xs text-gray-500">
                  <span v-if="uploadStates.get(feature.properties.local_uuid)?.status === 'uploading'">
                    {{ uploadStates.get(feature.properties.local_uuid)?.progress || 0 }}%
                  </span>
                  <span v-else-if="uploadStates.get(feature.properties.local_uuid)?.status === 'error'">
                    {{ uploadStates.get(feature.properties.local_uuid)?.error || 'Upload failed' }}
                  </span>
                  <span v-else-if="uploadStates.get(feature.properties.local_uuid)?.status === 'success'">
                    Complete
                  </span>
                  <span v-else>
                    Pending
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Actions -->
        <div v-if="failedCount > 0 && isComplete" class="space-y-2">
          <div class="flex items-center space-x-2">
            <Button
              @click="handleRetryFailed"
              size="sm"
              variant="outline"
            >
              <AlertTriangle class="w-4 h-4 mr-2" />
              Retry Failed ({{ failedCount }})
            </Button>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          @click="handleClose"
        >
          {{ isUploading ? 'Cancel Upload' : 'Close' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
